/**
 * Sprint T-17.1 · Notification Center · TemplatesTab.
 * Lista + preview + overrides (read-only).
 * §4.4
 */
import React, { useState } from "react";
import { X, FileText, Eye, Wand2 } from "lucide-react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useTemplates, useTemplateDetail, useTemplateOverrides } from "../../hooks/useNotifications";
import ChannelIcon from "./shared/ChannelIcon";
import { ModalShell } from "./shared/Dialogs";
import TemplateStudioEditor from "./shared/TemplateStudioEditor";

function TemplateCard({ template, onOpen, index }) {
  return (
    <button
      type="button"
      data-testid={`notif-template-card-${index}`}
      onClick={() => onOpen(template.template_id)}
      className="flex w-full flex-col items-start rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4 text-left transition-colors hover:border-[var(--ag-gold)]/50 hover:bg-[var(--ag-surface-2)]/40"
    >
      <div className="mb-2 flex w-full items-center justify-between">
        <ChannelIcon channel={template.channel} size={14} showLabel />
        <span className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] px-1.5 py-0.5 font-mono text-[9px]">
          {template.version}
        </span>
      </div>
      <div className="w-full">
        <div className="truncate font-mono text-xs font-semibold" title={template.template_id}>
          {template.template_id}
        </div>
        <div className="mt-1 text-[10px] text-[var(--ag-text-dim)]">
          topic: <span className="font-mono">{template.topic}</span>
        </div>
        {template.description && (
          <div className="mt-2 line-clamp-2 text-[10px] text-[var(--ag-text-dim)]">
            {template.description}
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          {(template.locales || []).map((loc) => (
            <span
              key={loc}
              className="rounded border border-[var(--ag-border)] px-1 py-0.5 font-mono text-[9px] text-[var(--ag-text-dim)]"
            >
              {loc}
            </span>
          ))}
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-[var(--ag-gold)]">
        <Eye className="h-3 w-3" /> Ver preview
      </div>
    </button>
  );
}

function TemplatePreviewFrame({ html, testId }) {
  // Iframe sandbox: allow same-origin ONLY (no scripts) · already-escaped output from backend Jinja sandbox
  const srcDoc = html
    ? `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;padding:12px;margin:0;color:#111;background:#fff}</style></head><body>${html}</body></html>`
    : "";
  if (!srcDoc) {
    return (
      <div className="rounded border border-dashed border-[var(--ag-border)] p-4 text-center text-xs text-[var(--ag-text-dim)]">
        Este template no genera HTML (webhook · body_text).
      </div>
    );
  }
  return (
    <iframe
      data-testid={testId || "notif-template-preview-frame"}
      title="Template HTML preview"
      sandbox=""
      srcDoc={srcDoc}
      style={{ width: "100%", height: 320, border: "1px solid var(--ag-border)", borderRadius: 6, background: "#fff" }}
    />
  );
}

function OverridesList({ templateId }) {
  const [tenant, setTenant] = useState("");
  const q = useTemplateOverrides(templateId, tenant || undefined);
  const items = q.data?.items || [];
  return (
    <div className="space-y-2" data-testid="notif-template-overrides">
      <div className="flex items-center gap-2">
        <input
          type="text"
          data-testid="notif-template-overrides-tenant"
          value={tenant}
          onChange={(e) => setTenant(e.target.value)}
          placeholder="Filtrar por tenant_id (opcional)"
          className="w-full rounded border border-[var(--ag-border)] bg-[var(--ag-surface)] px-2 py-1 font-mono text-[11px]"
        />
      </div>
      {q.isLoading && <LoadingState />}
      {q.data && items.length === 0 && (
        <div
          data-testid="notif-template-overrides-empty"
          className="rounded border border-dashed border-[var(--ag-border)] p-2 text-[10px] text-[var(--ag-text-dim)]"
        >
          Sin overrides por tenant · se usa el template base.
        </div>
      )}
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((o) => (
            <li key={o.id} className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-2 text-[10px]">
              <div className="font-mono text-xs">{o.tenant_id} · {o.locale}</div>
              <div className="mt-0.5 text-[var(--ag-text-dim)]">
                subject: {o.subject_override ? "override" : "base"} · html: {o.body_html_override ? "override" : "base"}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="text-[9px] italic text-[var(--ag-text-dim)]">
        T-17.1 solo lectura · edición de overrides queda para sprint futuro.
      </div>
    </div>
  );
}

function TemplateDrawer({ templateId, onClose }) {
  const q = useTemplateDetail(templateId);
  const d = q.data;
  const [mode, setMode] = useState("preview"); // preview | studio
  return (
    <ModalShell
      title={`Template · ${templateId}`}
      onClose={onClose}
      testId="notif-template-drawer"
      size="lg"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar el template" testId="notif-template-drawer-error" />}
      {d && (
        <div className="space-y-4" data-testid="notif-template-drawer-ok">
          {/* Mode toggle · Q8=c · one tab, two modes (preview / studio) */}
          <div
            className="flex gap-1 border-b border-[var(--ag-border)]"
            data-testid="notif-template-drawer-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setMode("preview")}
              data-testid="notif-template-drawer-mode-preview"
              className={`flex items-center gap-1 rounded-t px-3 py-1.5 text-[11px] ${
                mode === "preview"
                  ? "border-b-2 border-[var(--ag-gold)] text-[var(--ag-text)]"
                  : "text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              }`}
            >
              <Eye size={11} /> Preview
            </button>
            <button
              type="button"
              onClick={() => setMode("studio")}
              data-testid="notif-template-drawer-mode-studio"
              className={`flex items-center gap-1 rounded-t px-3 py-1.5 text-[11px] ${
                mode === "studio"
                  ? "border-b-2 border-[var(--ag-gold)] text-[var(--ag-text)]"
                  : "text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              }`}
            >
              <Wand2 size={11} /> Studio
            </button>
          </div>

          {mode === "preview" && (
            <>
              <section>
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
                  Preview (contexto de ejemplo · locale {d.preview?.locale})
                </h4>
                {d.preview?.subject && (
                  <div className="mb-2 rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-2">
                    <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Subject</div>
                    <div className="font-mono text-xs" data-testid="notif-template-preview-subject">
                      {d.preview.subject}
                    </div>
                  </div>
                )}
                {d.preview?.body_html && (
                  <TemplatePreviewFrame html={d.preview.body_html} />
                )}
                {!d.preview?.body_html && d.preview?.body_text && (
                  <pre
                    data-testid="notif-template-preview-text"
                    className="whitespace-pre-wrap rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-2 font-mono text-[10px]"
                  >
                    {d.preview.body_text}
                  </pre>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
                  Metadata
                </h4>
                <dl className="grid grid-cols-2 gap-2 text-[10px]">
                  <div>
                    <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Channel</dt>
                    <dd className="font-mono">{d.template?.channel}</dd>
                  </div>
                  <div>
                    <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Topic</dt>
                    <dd className="font-mono">{d.template?.topic}</dd>
                  </div>
                  <div>
                    <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Version</dt>
                    <dd className="font-mono">{d.template?.version}</dd>
                  </div>
                  <div>
                    <dt className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Default locale</dt>
                    <dd className="font-mono">{d.template?.default_locale}</dd>
                  </div>
                </dl>
                <div className="mt-2">
                  <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                    Required context ({(d.template?.required_context || []).length})
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {(d.template?.required_context || []).map((r) => (
                      <span key={r} className="rounded border border-[var(--ag-border)] px-1 py-0.5 font-mono text-[9px]">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              </section>

              <section>
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
                  Overrides por tenant (read-only · edítalos desde Studio →)
                </h4>
                <OverridesList templateId={templateId} />
              </section>
            </>
          )}

          {mode === "studio" && (
            <section>
              <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-gold)]">
                Template Studio · edición de overrides por tenant
              </h4>
              <TemplateStudioEditor
                template={d.template}
                baseContent={{
                  subject: d.preview?.subject,
                  body_html: d.preview?.body_html,
                  body_text: d.preview?.body_text,
                }}
                previewContext={d.template?.preview_context || {}}
              />
            </section>
          )}
        </div>
      )}
    </ModalShell>
  );
}

export default function TemplatesTab() {
  const q = useTemplates();
  const [openId, setOpenId] = useState(null);
  const items = q.data?.items || [];
  return (
    <section className="space-y-4" data-testid="notif-templates-tab">
      <WidgetCard
        title={`Registry · ${items.length} templates`}
        description="Hybrid catalog · base en código + overrides por tenant en BD"
        citations={["/api/notifications/templates"]}
        testId="notif-templates-registry"
      >
        {q.isLoading && <LoadingState />}
        {q.error && <EmptyState message="No se pudo cargar templates" testId="notif-templates-error" />}
        {q.data && items.length === 0 && (
          <EmptyState
            title="Sin templates"
            message="El registry está vacío · algo grave en backend."
            testId="notif-templates-empty"
          />
        )}
        {items.length > 0 && (
          <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-3">
            {items.map((t, i) => (
              <TemplateCard key={t.template_id} template={t} onOpen={setOpenId} index={i} />
            ))}
          </WidgetGrid>
        )}
      </WidgetCard>

      {openId && (
        <TemplateDrawer templateId={openId} onClose={() => setOpenId(null)} />
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["TEMPLATE_REGISTRY_V1 · 6 templates · Jinja2 sandbox · T-18 Studio editor integrado"]} />
      </footer>
    </section>
  );
}
